import AddIcon from "@mui/icons-material/Add";
import { Alert, Button, CircularProgress, Stack } from "@mui/material";
import { getFloatingNavigationScrollPaddingBottom } from "@src/appV2/redesign/components/FloatingNavigation/scrollPadding";
import { PullToRefresh } from "@src/appV2/redesign/components/PullToRefresh/PullToRefresh";
import { Text } from "@src/appV2/redesign/components/Text";
import { useHistory } from "react-router-dom";

import { type Case } from "../types";
import { getHomeHealthCaseMobileDetailPath, HOME_HEALTH_CREATE_CASE_PATH } from "../paths";
import { MobileCaseCard } from "./MobileCaseCard";
import { MobileHomeHealthPageHeader } from "./MobileHomeHealthPageHeader";

const createButtonSx = {
  borderRadius: "24px",
  py: 1.5,
  textTransform: "none",
  fontSize: "1rem",
} as const;

interface Props {
  cases: Case[];
  isLoading: boolean;
  isError: boolean;
  onRefresh: () => Promise<unknown> | unknown;
}

export function MobileCasesListView({ cases, isLoading, isError, onRefresh }: Props) {
  const history = useHistory();
  function renderBody() {
    if (isLoading) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "40vh" }}>
          <CircularProgress />
        </Stack>
      );
    }

    if (isError) {
      return (
        <Alert severity="error" sx={{ width: "100%" }}>
          Error loading cases.
        </Alert>
      );
    }

    if (cases.length === 0) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "40vh" }}>
          <Text variant="body1" sx={(theme) => ({ color: theme.palette.text.disabled })}>
            No cases yet
          </Text>
        </Stack>
      );
    }

    return (
      <Stack spacing={5}>
        {cases.map((caseItem) => (
          <MobileCaseCard
            key={caseItem.id}
            case={caseItem}
            onClick={() => {
              history.push(getHomeHealthCaseMobileDetailPath(caseItem.id));
            }}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack
      sx={(theme) => ({
        // Fill (not exceed) the fixed-height, overflow-hidden page wrapper so the
        // header stays put and only the list scrolls. minHeight:"100vh" forced the
        // column taller than the clipped parent, pushing the bottom of the scroll
        // region past the clip line so the last card couldn't be scrolled into view.
        height: "100%",
        minHeight: 0,
        bgcolor: "background.page",
        padding: theme.spacing(3),
      })}
    >
      <Stack
        sx={(theme) => ({
          paddingX: theme.spacing(3),
          gap: theme.spacing(4),
        })}
      >
        <MobileHomeHealthPageHeader title="Cases" />
      </Stack>
      <PullToRefresh
        onRefresh={onRefresh}
        disabled={isLoading}
        sx={(theme) => ({
          paddingTop: theme.spacing(4),
          paddingX: theme.spacing(3),
          // Clear the floating Menu pill / iOS home bar so the last card isn't
          // hidden behind it at the bottom of the scroll.
          paddingBottom: getFloatingNavigationScrollPaddingBottom(theme) ?? theme.spacing(4),
        })}
      >
        <Stack data-dd-privacy="mask" sx={(theme) => ({ gap: theme.spacing(4) })}>
          <Button
            fullWidth
            variant="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              history.push(HOME_HEALTH_CREATE_CASE_PATH);
            }}
            sx={createButtonSx}
          >
            Add Case
          </Button>
          {renderBody()}
        </Stack>
      </PullToRefresh>
    </Stack>
  );
}
